/**
 * Google Pay ローダー
 *
 * Google Pay JS API を動的に読み込み、fincode を gateway として利用する
 * PaymentsClient を初期化し、決済トークンを取得する。
 *
 * 参考:
 * - fincode Google Pay 仕様: https://docs.fincode.jp/payment/googlepay
 * - Google Pay API Web: https://developers.google.com/pay/api/web/overview
 *
 * 環境変数:
 * - VITE_GOOGLE_PAY_MERCHANT_ID: Google 販売者ID (例: BCR2DN5TU3P73SLW)
 *   未設定の場合はテスト環境用のダミー値を使用（TEST 環境ではチェックされない）
 */

// Google Pay API の型定義（最小限）
export interface GooglePayPaymentsClient {
  isReadyToPay: (request: object) => Promise<{ result: boolean }>;
  loadPaymentData: (request: object) => Promise<GooglePayPaymentData>;
  createButton: (options: GooglePayButtonOptions) => HTMLElement;
}

interface GooglePayPaymentData {
  paymentMethodData: {
    tokenizationData: {
      token: string;
    };
  };
}

interface GooglePayButtonOptions {
  onClick: () => void;
  buttonColor?: 'default' | 'black' | 'white';
  buttonType?: 'long' | 'short' | 'buy' | 'plain' | 'book' | 'checkout' | 'donate' | 'order' | 'pay' | 'subscribe';
  buttonSizeMode?: 'static' | 'fill';
  buttonLocale?: string;
}

interface GooglePaymentsApi {
  PaymentsClient: new (config: { environment: 'TEST' | 'PRODUCTION' }) => GooglePayPaymentsClient;
}

// window.google は GoogleLoginButton でも宣言されているため、キャストで取得する
const getGooglePaymentsApi = (): GooglePaymentsApi | undefined => {
  if (typeof window === 'undefined') return undefined;
  const g = (window as unknown as { google?: { payments?: { api?: GooglePaymentsApi } } }).google;
  return g?.payments?.api;
};

const GOOGLE_PAY_SCRIPT_URL = 'https://pay.google.com/gp/p/js/pay.js';
const GOOGLE_PAY_API_VERSION = 2;
const GOOGLE_PAY_API_VERSION_MINOR = 0;

// fincode Google Pay 対応ブランド
// fincode docs: VISA, Mastercard, JCB, Diners Club, American Express
// Google Pay API では DISCOVER も指定可能だが fincode 側で弾かれる可能性あり
const ALLOWED_CARD_NETWORKS = ['VISA', 'MASTERCARD', 'JCB', 'AMEX'];
// fincode は PAN_ONLY のみ対応（CRYPTOGRAM_3DS 非対応）
const ALLOWED_CARD_AUTH_METHODS = ['PAN_ONLY'];

let scriptLoadingPromise: Promise<void> | null = null;

/**
 * Google Pay JS SDK を動的に読み込む（1回だけ）
 */
const loadGooglePayScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window is not available'));
  }

  if (getGooglePaymentsApi()) {
    return Promise.resolve();
  }

  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_PAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Google Pay SDK の読み込みに失敗しました')));
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_PAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadingPromise = null;
      reject(new Error('Google Pay SDK の読み込みに失敗しました'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
};

export interface GooglePayInitOptions {
  /** fincode テナントショップID（gatewayMerchantId に利用） */
  fincodeShopId: string;
  /** サイトのテストモード（true: TEST 環境 / false: PRODUCTION 環境） */
  testMode: boolean;
  /** Google 販売者ID（未指定時は環境変数から取得） */
  googleMerchantId?: string;
  /** 店舗表示名（Google Pay シート上に表示） */
  merchantName?: string;
}

export interface GooglePayTransactionInfo {
  amount: number;
  merchantName?: string;
}

/**
 * Google Pay PaymentsClient を初期化
 */
export const initGooglePay = async (options: GooglePayInitOptions): Promise<GooglePayPaymentsClient> => {
  if (!options.fincodeShopId) {
    throw new Error('fincode ショップIDが設定されていません');
  }

  await loadGooglePayScript();

  const api = getGooglePaymentsApi();
  if (!api) {
    throw new Error('Google Pay SDK が利用できません');
  }

  return new api.PaymentsClient({
    environment: options.testMode ? 'TEST' : 'PRODUCTION',
  });
};

/**
 * PaymentDataRequest を組み立てる
 */
const buildPaymentDataRequest = (options: GooglePayInitOptions, transaction: GooglePayTransactionInfo) => {
  const googleMerchantId =
    options.googleMerchantId ||
    ((import.meta as unknown as { env?: { VITE_GOOGLE_PAY_MERCHANT_ID?: string } }).env?.VITE_GOOGLE_PAY_MERCHANT_ID ?? '');

  return {
    apiVersion: GOOGLE_PAY_API_VERSION,
    apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
          allowedCardNetworks: ALLOWED_CARD_NETWORKS,
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'fincode',
            gatewayMerchantId: options.fincodeShopId,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: googleMerchantId,
      merchantName: transaction.merchantName || options.merchantName || 'JOYBASE',
    },
    transactionInfo: {
      currencyCode: 'JPY',
      totalPrice: transaction.amount.toString(),
      totalPriceStatus: 'FINAL' as const,
      countryCode: 'JP',
    },
  };
};

/**
 * Google Pay が利用可能かチェック
 */
export const isGooglePayReady = async (options: GooglePayInitOptions): Promise<boolean> => {
  try {
    const client = await initGooglePay(options);
    const response = await client.isReadyToPay({
      apiVersion: GOOGLE_PAY_API_VERSION,
      apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
            allowedCardNetworks: ALLOWED_CARD_NETWORKS,
          },
        },
      ],
    });
    return response.result;
  } catch (error) {
    console.error('Google Pay isReadyToPay check failed:', error);
    return false;
  }
};

/**
 * Google Pay ボタンをマウント先に追加
 *
 * @returns cleanup function (ボタンを DOM から削除)
 */
export const mountGooglePayButton = async (
  target: HTMLElement,
  options: GooglePayInitOptions,
  onClick: (client: GooglePayPaymentsClient) => void
): Promise<() => void> => {
  const client = await initGooglePay(options);
  const button = client.createButton({
    onClick: () => onClick(client),
    buttonColor: 'default',
    buttonType: 'pay',
    buttonSizeMode: 'fill',
    buttonLocale: 'ja',
  });

  // 既存のボタンがあれば削除してから追加（再マウント対応）
  while (target.firstChild) {
    target.removeChild(target.firstChild);
  }
  target.appendChild(button);

  return () => {
    if (target.contains(button)) {
      target.removeChild(button);
    }
  };
};

/**
 * Google Pay シートを表示してトークンを取得
 *
 * @returns Base64 エンコード済みのトークン（fincode の execute_payment API に渡す）
 */
export const requestGooglePayToken = (
  client: GooglePayPaymentsClient,
  options: GooglePayInitOptions,
  transaction: GooglePayTransactionInfo
): Promise<string> => {
  const paymentDataRequest = buildPaymentDataRequest(options, transaction);

  // Safariでもユーザー操作を維持できるよう、クリックハンドラ内で
  // awaitを挟まず直ちにloadPaymentDataを呼び出す。
  return client.loadPaymentData(paymentDataRequest).then((paymentData) => {
    const rawToken = paymentData.paymentMethodData.tokenizationData.token;

    if (!rawToken) {
      throw new Error('Google Pay トークンの取得に失敗しました');
    }

    // fincode は Base64 エンコードされたトークンを期待する
    return btoa(rawToken);
  });
};
